import { useEffect, useState } from "react";
import { CalendarClock, Loader2, Lock, Unlock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/common";
import { adminApi, errMsg, fmtDateTime } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { OverdueRuleBox } from "./OverdueRuleBox";

const MODES = [
  ["total", "Bloquear o sistema e o cardápio online", "Ninguém do cliente consegue entrar e o cardápio/delivery sai do ar."],
  ["login", "Bloquear só o login do sistema", "O cardápio online continua recebendo pedidos."],
];
export const MODE_SHORT = { total: "sistema e cardápio", login: "só login" };
const nowLocal = () => new Date(Date.now() - new Date().getTimezoneOffset() * 6e4).toISOString().slice(0, 16);

function Choice({ on, onClick, title, text, testId }) {
  return (
    <button type="button" onClick={onClick} className={cn("w-full rounded-lg border p-3 text-left transition-colors", on ? "border-primary bg-primary/5" : "hover:bg-muted")} data-testid={testId}>
      <p className="font-semibold">{title}</p>{text && <p className="text-xs text-muted-foreground">{text}</p>}
    </button>
  );
}

function ScheduleInfo({ s, busy, onCancel }) {
  return (
    <div className="flex items-center gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-amber-600 dark:text-amber-400" data-testid="access-schedule-info">
      <CalendarClock className="h-4 w-4 shrink-0" />
      <span className="flex-1">Bloqueio agendado para <b>{fmtDateTime(s.at)}</b> ({MODE_SHORT[s.mode]}).</span>
      <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel} data-testid="access-schedule-cancel"><X className="h-4 w-4 mr-1" />Cancelar</Button>
    </div>
  );
}

export function TenantAccessDialog({ tenant, onOpenChange, onSaved }) {
  const [mode, setMode] = useState("total");
  const [reason, setReason] = useState("");
  const [when, setWhen] = useState("now");
  const [at, setAt] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setMode(tenant?.block_schedule?.mode || tenant?.block_mode || "total");
    setReason(tenant?.blocked_reason || tenant?.block_schedule?.reason || "");
    setWhen("now"); setAt("");
  }, [tenant]);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onSaved(); onOpenChange(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const access = (blocked) => run(() => adminApi.patch(`/tenants/${tenant.id}/access`, { blocked, mode, reason }), blocked ? "Acesso bloqueado" : "Acesso liberado");
  const schedule = () => run(() => adminApi.put(`/tenants/${tenant.id}/block-schedule`, { at, mode, reason }), "Bloqueio agendado");
  const cancel = () => run(() => adminApi.delete(`/tenants/${tenant.id}/block-schedule`), "Agendamento cancelado");
  const later = !tenant?.blocked && when === "later";
  return (
    <Dialog open={!!tenant} onOpenChange={(v) => !v && onOpenChange()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="tenant-access-dialog">
        <DialogHeader><DialogTitle>Acesso · {tenant?.nome_fantasia}</DialogTitle></DialogHeader>
        {tenant && (
          <div className="space-y-4 text-sm">
            {tenant.blocked && <p className="rounded-lg bg-red-500/10 px-3 py-2 text-red-500" data-testid="access-blocked-info">{tenant.blocked_by === "overdue" ? "Bloqueado automaticamente por atraso" : "Bloqueado"} desde {fmtDateTime(tenant.blocked_at)}.</p>}
            {tenant.block_schedule && <ScheduleInfo s={tenant.block_schedule} busy={busy} onCancel={cancel} />}
            <div className="space-y-2">{MODES.map(([v, title, text]) => <Choice key={v} on={mode === v} onClick={() => setMode(v)} title={title} text={text} testId={`access-mode-${v}`} />)}</div>
            {!tenant.blocked && (
              <div className="grid grid-cols-2 gap-2">
                <Choice on={when === "now"} onClick={() => setWhen("now")} title="Bloquear agora" testId="access-when-now" />
                <Choice on={when === "later"} onClick={() => setWhen("later")} title="Agendar data" testId="access-when-later" />
              </div>
            )}
            {later && <Field label="Bloquear em (horário de Brasília)"><Input type="datetime-local" min={nowLocal()} value={at} onChange={(e) => setAt(e.target.value)} data-testid="access-schedule-at" /></Field>}
            <Field label="Motivo (opcional, só para seu controle)"><Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} data-testid="access-reason-input" /></Field>
            <p className="text-xs text-muted-foreground">{later ? "Na data escolhida o bloqueio acontece sozinho e as sessões abertas do cliente são encerradas." : "Ao bloquear, todas as sessões abertas do cliente são encerradas na hora."}</p>
            <div className="flex flex-wrap gap-2">
              {tenant.blocked && <Button variant="outline" className="flex-1" disabled={busy} onClick={() => access(false)} data-testid="access-unblock-btn"><Unlock className="h-4 w-4 mr-1" />Liberar acesso</Button>}
              {later ? (
                <Button className="flex-1" disabled={busy || !at} onClick={schedule} data-testid="access-schedule-btn">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-1" />}Agendar bloqueio</Button>
              ) : (
                <Button variant="destructive" className="flex-1" disabled={busy} onClick={() => access(true)} data-testid="access-block-btn">
                  {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Lock className="h-4 w-4 mr-1" />}{tenant.blocked ? "Atualizar bloqueio" : "Bloquear agora"}
                </Button>
              )}
            </div>
            <OverdueRuleBox key={tenant.id} tenant={tenant} onSaved={onSaved} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
